if (!customElements.get('newsletter-form')) {
  customElements.define(
    'newsletter-form',
    class NewsletterForm extends HTMLElement {
      static LOADING_DELAY = 500;

      constructor() {
        super();

        this.successElementId = this.getAttribute('success-element-id');
        this.contentElementId = this.getAttribute('content-element-id');
        this.newsletterId = this.getAttribute('newsletter-id');
        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('input[type="email"]');
        this.acceptInput = this.querySelector('input[type="checkbox"]');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.isCurrentNewsletterActive = this.isCurrentNewsletter();

        if (this.submitButton) {
          this.submitButton.disabled = false;
        }

        this._onEmailBlur = this.onEmailBlurHandler.bind(this);
        this._onInputFocus = this.onInputFocusHandler.bind(this);
        this._onAcceptChange = this.onAcceptChangeHandler.bind(this);
        this._onButtonClick = this.onButtonClickHandler.bind(this);
      }

      connectedCallback() {
        this.emailInput?.addEventListener('blur', this._onEmailBlur);
        this.emailInput?.addEventListener('focus', this._onInputFocus);
        this.acceptInput?.addEventListener('focus', this._onInputFocus);
        this.acceptInput?.addEventListener('change', this._onAcceptChange);

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this._onButtonClick);
        }

        this._unsubscribeNewsletterActivated = subscribe(
          'newsletter-activated',
          this.onNewsletterActivated.bind(this)
        );

        this.handleShowSuccessMessage();
        this.handleNewsletterContent();
      }

      disconnectedCallback() {
        this.emailInput?.removeEventListener('blur', this._onEmailBlur);
        this.emailInput?.removeEventListener('focus', this._onInputFocus);
        this.acceptInput?.removeEventListener('focus', this._onInputFocus);
        this.acceptInput?.removeEventListener('change', this._onAcceptChange);
        this.submitButton?.removeEventListener('click', this._onButtonClick);
        this._unsubscribeNewsletterActivated?.();
      }

      onNewsletterActivated({ formId }) {
        if (formId !== this.newsletterId) return;
        this.isCurrentNewsletterActive = true;
      }

      onEmailBlurHandler() {
        this.validateInput(
          this.emailInput,
          this.validateEmail(this.emailInput.value.trim())
        );
      }

      onInputFocusHandler(event) {
        this.hideInputError(event.target);
      }

      onAcceptChangeHandler() {
        this.validateInput(this.acceptInput, this.acceptInput.checked);
      }

      onButtonClickHandler(event) {
        this.toggleSpinner(true);

        const emailValid = this.validateInput(
          this.emailInput,
          this.validateEmail(this.emailInput.value.trim())
        );
        const acceptValid = this.validateInput(
          this.acceptInput,
          this.acceptInput.checked
        );

        if (!emailValid || !acceptValid) {
          event.preventDefault();
          setTimeout(() => {
            this.toggleSpinner(false);
          }, NewsletterForm.LOADING_DELAY);
        }
      }

      toggleSpinner(isLoading) {
        this.submitButton?.classList.toggle('loading', isLoading);
        this.submitButton
          ?.querySelector('.loading__spinner')
          ?.classList.toggle('hidden', !isLoading);
      }

      handleNewsletterContent() {
        const contentElement = document?.getElementById(this.contentElementId);
        const newsletterStatus =
          contentElement?.dataset?.newsletterStatus || 'false';
        const isForce =
          newsletterStatus == 'true' && this.isCurrentNewsletterActive;

        if (!contentElement) {
          return null;
        }

        contentElement.classList.toggle('hidden', isForce);
      }

      handleShowSuccessMessage() {
        const successElement = document?.getElementById(this.successElementId);
        const newsletterStatus =
          successElement.dataset.newsletterStatus || 'false';
        const isForce =
          newsletterStatus == 'true' && this.isCurrentNewsletterActive;

        if (!successElement) {
          return null;
        }

        successElement.classList.toggle('hidden', !isForce);
      }

      validateInput(input, isValid) {
        if (isValid) {
          this.hideInputError(input);
        } else {
          const errorType =
            input.type === 'email' && input.value.trim() ? 'email' : 'required';
          this.renderInputError(input, errorType);
        }

        return isValid;
      }

      validateEmail(email) {
        const emailRegex =
          /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
      }

      getTargetElement(input) {
        return input.type === 'checkbox' ? input.parentNode : input;
      }

      getErrorMessage(input, type = 'Required') {
        const { dataset } = input;
        const errorDataType = `error${
          type.charAt(0).toUpperCase() + type.slice(1)
        }`;
        return dataset[errorDataType] || '';
      }

      getURLHashValue() {
        const queryString = window.location.search;
        const hashValue = window.location.hash?.split('#')[1];
        const urlParams = new URLSearchParams(queryString);
        const customerPosted = urlParams.get('customer_posted');
        const isCustomerPosted = customerPosted == 'true';

        return isCustomerPosted ? hashValue : null;
      }

      isCurrentNewsletter() {
        const hashValue = this.getURLHashValue();

        return this.newsletterId == hashValue;
      }

      updateErrorMessage(input, message, hideError = true) {
        if (!input) return;

        const target = this.getTargetElement(input);
        const error = target.nextElementSibling;

        if (
          error &&
          error.classList &&
          error.classList.contains('js-input-error')
        ) {
          error.textContent = message;
          error.setAttribute('aria-hidden', hideError);
        }
      }

      renderInputError(input, type = 'Required') {
        if (!input) return;

        this.hideInputError(input);
        input.classList?.add('has-error');
        this.updateErrorMessage(
          input,
          this.getErrorMessage(input, type),
          false
        );
      }

      hideInputError(input) {
        if (!input) return;

        input.classList?.remove('has-error');
        this.updateErrorMessage(input, '', true);
      }
    }
  );
}
